"use client";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { ModalOverlay } from "@/components/ModalOverlay";

type HelpItem = { term: string; desc: string };

const HelpIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M12 17.25h.007v.008H12v-.008ZM21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

// Per-page help. Content lives in the i18n `help.<topic>` namespace.
// - variant "inline" (default): a "?" button meant to sit at the right of a page
//   title row. Caller may override styling via `className`.
// - variant "corner": a fixed top-right button for pages without a title row
//   (e.g. the centered login/register screens).
export function PageHelp({
  topic,
  variant = "inline",
  className,
}: {
  topic: string;
  variant?: "inline" | "corner";
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const t = useTranslations("help");
  const items = (t.raw(`${topic}.items`) as HelpItem[]) ?? [];

  return (
    <>
      {variant === "corner" ? (
        <button
          onClick={() => setOpen(true)}
          aria-label={t("aria")}
          title={t("aria")}
          className="fixed top-5 right-5 z-40 inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors shadow-sm"
        >
          <HelpIcon className="w-5 h-5" />
        </button>
      ) : (
        <button
          onClick={() => setOpen(true)}
          aria-label={t("aria")}
          title={t("aria")}
          className={
            className ??
            "inline-flex items-center justify-center w-9 h-9 rounded-full border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors shrink-0"
          }
        >
          <HelpIcon className="w-5 h-5" />
        </button>
      )}

      {open && (
        <ModalOverlay
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
          onEscape={() => setOpen(false)}
        >
          <div
            className="relative bg-[var(--surface)] rounded-2xl border border-[var(--border)] shadow-2xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-lora)" }}>
                {t(`${topic}.title`)}
              </h3>
              <button
                onClick={() => setOpen(false)}
                aria-label={t("close")}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors p-1 -mr-1 -mt-1 shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className="text-sm text-[var(--text-secondary)] mb-5 leading-relaxed">{t(`${topic}.intro`)}</p>

            <ul className="space-y-3.5">
              {items.map((it, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full bg-[var(--accent)] shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[var(--text-primary)]">{it.term}</p>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5 leading-relaxed">{it.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            <p className="text-xs text-[var(--text-muted)] italic mt-5 pt-4 border-t border-[var(--border)] leading-relaxed">
              {t(`${topic}.closing`)}
            </p>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
